// @flow
import React from 'react'
import { Link } from '@ta-interaktiv/contentbricks'
import AnchorLink from 'react-anchor-link-smooth-scroll'
import './styles.scss'

export class Methodology extends React.Component<Props, State> {
  constructor() {
    super()
    this.state = {
      isLimited: true
    }
  }

  showMore() {
    this.setState({ isLimited: false })
    // track in google analytics
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      event: 'Interactions',
      event_action: 'click',
      event_label: `show-more: Methodology`
    })
  }

  render() {
    return (
      <div className='method ui vertical segment dark' id='methodik'>
        <div
          className={
            this.state.isLimited
              ? 'limited ui text methodik container'
              : 'ui text methodik container'
          }
        >
          <div className='ta text'>
            <h4>Methodik und Modellbeschreibung</h4>

            <h5>Berechnung des Gletscherrückgangs</h5>
            <p>
              Die Entwicklung der Gletscher beruht auf einem Berechnungsmodell,
              das die Prozesse beschreibt, über welche das Eis auf eine
              Klimaveränderung reagiert (
              <AnchorLink href='#foot-1' className='nav-link'>
                1
              </AnchorLink>
              ). Einerseits wird berechnet, wie viel Schnee an jedem Punkt auf
              dem Gletscher fällt, andererseits wie viel aufgrund von höheren
              Temperaturen abschmilzt. Schliesslich wird bestimmt, wie sich die
              Eismasse durch das Fliessen bewegt und verändert. Die Berechnungen
              werden mit einer Vielzahl von Beobachtungsdaten aus den letzten
              Jahrzehnten überprüft, sodass realistische Vorhersagen in der
              Zukunft möglich sind. Obwohl der Ansatz dem aktuellsten Stand der
              Wissenschaft entspricht, führen nötige Vereinfachungen zu gewissen
              Unsicherheiten in den Resultaten. Das verwendete
              Berechnungsverfahren wurde auf alle Gletscher der Schweiz
              angewandt (
              <AnchorLink href='#foot-2' className='nav-link'>
                2
              </AnchorLink>
              ), und fünf Gletscher wurden ausgewählt.
            </p>
            <h5>Visualisierung</h5>
            <p>
              Die Visualisierungen basieren auf einem digitalen Modell der
              Geländeoberfläche, erstellt vom Bundesamt für Landestopografie
              (Swisstopo). Zusätzlich wird die aktuelle Ausdehnung der Gletscher
              verwendet (
              <AnchorLink href='#foot-3' className='nav-link'>
                3
              </AnchorLink>
              ). Zuerst werden die Gletscher vollständig entfernt, um das
              Grundgestein darunter freizulegen. Die Eisdicke ist aus Messungen
              mit Radar und Modellrechnungen bekannt (
              <AnchorLink href='#foot-4' className='nav-link'>
                4
              </AnchorLink>
              ). Mit der Form des eisfreien Untergrunds kann die
              voraussichtliche Lage zukünftiger Seen und Bäche bestimmt werden.
              Das Geländemodell wird dann mit einer Luftaufnahme überlagert. Das
              Bild wird so verarbeitet, dass es Felsen und Wasser anstelle von
              Schnee und Eis zeigt. Schließlich wird die Gletscheroberfläche,
              die für jedes Jahr des Zeitraums 2022-2100 berechnet worden ist,
              in Weiß über dem Gelände hinzugefügt.
            </p>
            <div className='sources'>
              <h5>Datenquellen</h5>
              <ul>
                <li>
                  Bundesamt für Landestopografie swisstopo - swissAlti3D,
                  SWISSIMAGE
                </li>
                <li>
                  Region Lombardei - DTM 5X5 - Modello digitale del terreno (ed.
                  2015);{' '}
                  <Link
                    href='https://creativecommons.org/licenses/by/4.0/deed.it'
                    hideIcon
                  >
                    Lizenz
                  </Link>
                </li>
                <li>
                  Region Aostatal - Digital Terrain Model (DTM);{' '}
                  <Link
                    href='https://creativecommons.org/licenses/by/4.0/deed.it'
                    hideIcon
                  >
                    Lizenz
                  </Link>
                </li>
                <li>
                  Land Vorarlberg - 
                  <Link href='https://data.vorarlberg.gv.at' hideIcon>
                    data.vorarlberg.gv.at
                  </Link>
                  ;{' '}
                  <Link
                    href='https://creativecommons.org/licenses/by/4.0/deed.de'
                    hideIcon
                  >
                    Lizenz
                  </Link>
                </li>
              </ul>
            </div>
            <h5>Klimaentwicklung</h5>
            <p>
              Die künftige Entwicklung der Gletscher wird für zwei
              Klimaszenarien während des 21. Jahrhunderts berechnet. Diese
              Szenarien wurden vom Weltklimarat definiert. Das optimistische
              Szenario <span className='ui green label small'>SSP1-1.9</span>{' '}
              beschreibt eine Welt, in der die globalen CO
              <sub>2</sub>-Emissionen bis etwa 2050 auf netto null gesenkt
              werden. Damit wird das Ziel des Pariser Abkommens erreicht, die
              vom Menschen verursachte globale Erwärmung auf 1,5 Grad Celsius
              über den vorindustriellen Temperaturen zu begrenzen. Das
              pessimistische Szenario{' '}
              <span className='ui red label'>SSP5-8.5</span> zeigt eine Zukunft
              mit Wirtschaftswachstum, das durch die Verbrennung von Öl, Gas und
              Kohle angetrieben wird, mit einer prognostizierten weltweiten
              Erwärmung von 4,4 Grad Celsius bis zum Ende des Jahrhunderts.
            </p>
            <div className='references'>
              <h5>Referenzen</h5>
              <p id='foot-1'>
                (1) Huss, M. and Hock, R. (2015). A new model for global glacier
                change and sea-level rise. Frontiers in Earth Science, 3:54,
                doi:10.3389/feart.2015.00054.
              </p>
              <p id='foot-2'>
                (2) Steffen, T., Huss, M., Estermann, R., Hodel, E., and
                Farinotti, D. (in review). Volume, formation and sedimentation
                of future glacier lakes in Switzerland. Earth Surface Dynamics
                Discussions, doi:10.5194/esurf-2022-12.
              </p>
              <p id='foot-3'>
                (3) Linsbauer, A., Huss, M., Hodel, E., Bauder, A., Fischer, M.,
                Weidmann, Y., Baertschi, H. and Schmassmann, E. (2021). The new
                Swiss Glacier Inventory SGI2016: From a topographic to a
                glaciological dataset. Frontiers in Earth Science, 9, 774,
                doi:10.3389/feart.2021.704189.
              </p>
              <p id='foot-4'>
                (4) Grab, M., Mattea, E., Bauder, B., Huss, M., Rabenstein, L.,
                Hodel, E., Linsbauer, A., Hellmann, S., Church, G., Langhammer,
                L., Schmid, L., Deleze, K, Schaer, P., Lathion, P., Farinotti,
                D., Maurer, H. (2021). Ice thickness distribution of all Swiss
                glaciers based on extended ground-penetrating radar data and
                glaciological modeling. Journal of Glaciology, 1-19,
                doi:10.1017/jog.2021.55.
              </p>
            </div>
          </div>
        </div>
        {this.state.isLimited && (
          <div className='showmore'>
            <button onClick={() => this.showMore()} className='ui button'>
              mehr zeigen
            </button>
          </div>
        )}
      </div>
    )
  }
}
